import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState } from 'react';
import { filesize } from 'filesize';
import { Flex, Body, Button, Infobox } from '@cognite/cogs-core';
import { DocumentIcon, DeleteIcon, CloseIcon } from '@cognite/cogs-icons';
import { IconWrapper } from '@cognite/cogs-utils';
import { ProgressBar } from '../progress-bar/progress-bar.js';

var UploadedItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var file = _ref.file,
    progressProps = _ref.progress,
    error = _ref.error,
    onRemove = _ref.onRemove,
    onClose = _ref.onClose,
    icon = _ref.icon;
  var progress = progressProps || 100;
  var inputProgress = progress > 100 || progress < 0 ? 100 : progress;
  var fileSize = filesize(file.size, {
    standard: 'jedec'
  });
  var partialFileSize = filesize(file.size * (inputProgress / 100), {
    standard: 'jedec'
  });
  var isProgressBarEnabled = progress !== 100;
  var onClick = function onClick(evt) {
    if (progress === 100 && !error) {
      onRemove === null || onRemove === undefined ? undefined : onRemove(file);
    } else {
      onClose === null || onClose === undefined ? undefined : onClose(evt);
    }
  };
  return jsxs(Flex, {
    "data-testid": file.name,
    ref: ref,
    gap: 8,
    className: "cogs-lab-uploaded-files-file",
    children: [icon ? icon : jsx(IconWrapper, {
      className: "cogs-lab-fileupload-document-wrapper",
      size: 12,
      children: jsx(DocumentIcon, {})
    }), jsxs(Flex, {
      flex: 1,
      direction: "column",
      gap: isProgressBarEnabled ? 6 : 0,
      className: "cogs-lab-fileupload-file-details-container",
      children: [jsx(Body, {
        size: "small",
        className: "cogs-lab-fileupload-filename",
        children: file.name
      }), isProgressBarEnabled && jsx(ProgressBar, {
        value: progress
      }), error ? jsx(Body, {
        role: "status",
        "aria-live": "polite",
        size: "x-small",
        className: "cogs-lab-fileupload-error-item",
        children: error
      }) : jsxs(Flex, {
        justifyContent: "space-between",
        alignItems: "center",
        children: [jsx(Body, {
          className: "cogs-lab-fileupload-filesize",
          size: "x-small",
          children: progress === 100 ? fileSize : "".concat(partialFileSize, " of ").concat(fileSize)
        }), progress !== 100 && jsxs(Body, {
          size: "x-small",
          className: "cogs-lab-fileupload-filesize",
          children: [progress, "%"]
        })]
      })]
    }), jsx(Flex, {
      alignItems: "center",
      direction: "column",
      justifyContent: progress === 100 && !error ? 'flex-start' : 'center',
      children: jsx(Button, {
        className: "cogs-lab-fileupload-remove-button",
        size: "small",
        type: "ghost",
        onClick: onClick,
        icon: progress === 100 && !error ? jsx(DeleteIcon, {}) : jsx(CloseIcon, {}),
        "aria-label": "".concat(progress === 100 && !error ? 'Delete' : 'Close', " ").concat(file.name)
      })
    })]
  });
});
var MultipleUploadedTable = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var _ref2$files = _ref2.files,
    files = _ref2$files === undefined ? [] : _ref2$files,
    onRemoveAll = _ref2.onRemoveAll,
    onRemove = _ref2.onRemove;
  // Add id to each file to avoid re-rendering and  as some files might have the same name
  var filesWithId = files.map(function (file) {
    return Object.assign({
      id: crypto.randomUUID()
    }, file);
  });
  var someError = filesWithId.some(function (file) {
    return file.error;
  });
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isErrorInfoboxVisible = _useState2[0],
    setIsErrorInfoboxVisible = _useState2[1];
  return files.length > 0 ? jsxs(Flex, {
    gap: 12,
    direction: "column",
    ref: ref,
    className: "cogs-lab-uploaded-files",
    children: [jsxs(Flex, {
      justifyContent: "space-between",
      alignItems: "center",
      children: [jsx(Body, {
        size: "small",
        strong: true,
        children: "Uploaded Files"
      }), jsx(Button, {
        className: "cogs-lab-fileupload-remove-button",
        size: "small",
        type: "ghost",
        onClick: onRemoveAll,
        children: "Clear All"
      })]
    }), someError && isErrorInfoboxVisible && jsx(Infobox, {
      status: "critical",
      onClose: function onClose() {
        return setIsErrorInfoboxVisible(false);
      },
      compact: true,
      children: "Certain files couldn\u2019t be uploaded"
    }), jsx(Flex, {
      role: "list",
      "data-testid": "uploaded-files",
      gap: 8,
      className: "cogs-lab-fileupload-listwrapper",
      direction: "column",
      children: filesWithId.map(function (_ref3) {
        var file = _ref3.file,
          id = _ref3.id,
          error = _ref3.error,
          progress = _ref3.progress;
        return jsx(UploadedItem, {
          file: file,
          onRemove: onRemove,
          progress: progress,
          error: error
        }, id);
      })
    })]
  }) : null;
});
MultipleUploadedTable.displayName = 'FileUpload.UploadedItems';
var SingleUploadedFile = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var file = _ref4.file,
    error = _ref4.error,
    progress = _ref4.progress,
    onRemove = _ref4.onRemove,
    icon = _ref4.icon;
  return jsx("div", {
    className: "cogs-lab-fileupload-uploaded-file",
    children: jsx(UploadedItem, {
      ref: ref,
      file: file,
      icon: icon,
      progress: progress,
      onRemove: onRemove,
      error: error
    })
  });
});
SingleUploadedFile.displayName = 'FileUpload.UploadedItem';

export { MultipleUploadedTable, SingleUploadedFile };
